import {
	createAsyncThunk,
	createSlice,
	PayloadAction,
} from '@reduxjs/toolkit';
import { uniqueId } from 'lodash';
import {
	calculateEstimatesThunk,
	createComplaintThunk,
	createFraudThunk,
	deleteComplaintThunk,
	deleteFraudThunk,
	getClaimDetailsThunk,
	getReasonCodeCanceledThunk,
	getReasonCodePendingThunk,
	getReasonCodeRejectThunk,
	getReasonCodeVoidThunk,
	rekeyClaimThunk,
	returnClaimThunk,
	sendToReviewThunk,
	updateClaimCommentThunk,
	updateClaimStatusThunk,
	updateComplaintThunk,
	updateFraudThunk,
	voidClaimThunk,
} from './ClaimThunk';

import {
	deleteDocument,
	editDocumentName,
	uploadDocument,
} from 'feature/upload/uploadSlice';
import { showSnackBar } from 'styles/snackbar/showSnackBar';
import palette from 'styles/theme/Palette';
import { ECarriersList } from 'types/Carrier';
import {
	IClaim,
	IClaimAccumulator,
	IClaimComponents,
	IClaimState,
	RECEIPT_BASE_DATA,
	TStatusCalculation,
} from 'types/Claim';
import { extractReviewRetailNetOfTax } from 'utils/utils';

const initialState: IClaimState = {
	claimData: {
		carrierIntegrationFlow: ECarriersList.BASIC_CARRIER,
		customLayout: ECarriersList.BASIC_CARRIER,
		claimInNetwork: false,
		claimUUID: undefined,
		externalClaimId: '',
		beneficiaryMemberId: '',
		memberId: '',
		beneficiarySubscriptionUuid: '',
		memberSubscriptionUuid: '',
		providerId: '',
		claimDate: '',
		step: 0,
		status: {
			id: 1,
			name: '',
			label: '',
			groupCode: '',
			groupLabel: '',
			reasonCode: '',
			reasonCodeLabel: '',
		},
		// page 1
		exam: 0,
		materials: 0,
		lensType: undefined,
		rxChangedWithoutOldPrescription: null,
		//page 2
		newPrescriptionContent: undefined,
		oldPrescriptionContent: undefined,
		prescriptionConsent: false,
		//page 3
		amount: {
			//basically everything is passed by calculate. only serviceid and retail amount is not
			components: [],
			total: {
				retailAmount: '',
				total: undefined,
				contractualAdjustmentAmount: undefined,
				memberPayAmount: undefined,
				planPayAmount: undefined,
				serviceName: undefined,
				serviceId: null,
				discountedAmount: undefined,
				assessment: undefined,
				allowablePlanPay: undefined,
				coPay: undefined,
				coInsurance: undefined,
				disallowedAmount: undefined,
				decliningBalance: undefined,
				claimCostReduction: undefined,
				inNetworkSave: undefined,
				retailNetOfTax: '',
				tax: '',
			},
		},
		rxChangeAttestation: undefined,
		rxAttestation: undefined,
		attachments: {
			prescription: undefined,
			others: undefined,
			receipt: undefined,
			complaint: undefined,
			fraud: undefined,
		},
		receipt: {
			number: '',
			name: '',
			vatCode: '',
			receiptDate: '',
			totalInvoiceAmount: '',
			...RECEIPT_BASE_DATA,
		},
		uploadConsent: false,
		internalNote: '',
		reviewClaim: null,
		claimCarrierIntegrationInfo: {
			carrierClaimId: null,
			customFaschimExtraInfo: null,
			memberFiscalCode: null,
		},
		comments: '',
	},
	rekeyClaim: undefined,
	errorCode: 0,
	errorMessage: '',
	triggerRefresh: true,
	calculated: false,
	redirect: false,
	doctorTypes: [],
	reasonCodesVoid: [],
	reasonCodesCanceled: [],
	reasonCodesPending: [],
	reasonCodesReject: [],
	servicesMap: [],
	reloadData: false,
	showClaimComment: false,
	validationErrors: [],
	statusCalculation: 'beforeCalculation',
	otherServices: [],
};

export const getClaimDetails = createAsyncThunk(
	'claim/getClaimDetails',
	getClaimDetailsThunk,
);

export const calculateEstimates = createAsyncThunk(
	'claim/calculateEstimates',
	calculateEstimatesThunk,
);

export const updateClaimStatus = createAsyncThunk(
	'claim/updateClaimStatus',
	updateClaimStatusThunk,
);

export const createComplaint = createAsyncThunk(
	'claim/createComplaint',
	createComplaintThunk,
);

export const createFraud = createAsyncThunk(
	'claim/createFraud',
	createFraudThunk,
);

export const deleteComplaint = createAsyncThunk(
	'claim/deleteComplaint',
	deleteComplaintThunk,
);

export const deleteFraud = createAsyncThunk(
	'claim/deleteFraud',
	deleteFraudThunk,
);

export const updateComplaint = createAsyncThunk(
	'claim/updateComplaint',
	updateComplaintThunk,
);

export const updateFraud = createAsyncThunk(
	'claim/updateFraud',
	updateFraudThunk,
);

export const rekeyClaimAction = createAsyncThunk(
	'claim/rekeyClaim',
	rekeyClaimThunk,
);

export const getReasonCodeVoid = createAsyncThunk(
	'claim/getReasonCodeVoid',
	getReasonCodeVoidThunk,
);

export const getReasonCodeCanceled = createAsyncThunk(
	'claim/getReasonCodeCanceled',
	getReasonCodeCanceledThunk,
);

export const getReasonCodePending = createAsyncThunk(
	'claim/getReasonCodePending',
	getReasonCodePendingThunk,
);

export const getReasonCodeReject = createAsyncThunk(
	'claim/getReasonCodeReject',
	getReasonCodeRejectThunk,
);

export const voidClaim = createAsyncThunk('claim/voidClaim', voidClaimThunk);

export const sendToReview = createAsyncThunk(
	'claim/sendToReview',
	sendToReviewThunk,
);

export const returnClaim = createAsyncThunk(
	'claim/returnClaim',
	returnClaimThunk,
);

export const updateClaimComment = createAsyncThunk(
	'claim/updateClaimComment',
	updateClaimCommentThunk,
);

const claimSlice = createSlice({
	name: 'claim',
	initialState,
	reducers: {
		resetClaim: state => ({
			...initialState,
			reasonCodesCanceled: state.reasonCodesCanceled,
			reasonCodesVoid: state.reasonCodesVoid,
			reasonCodesPending: state.reasonCodesPending,
			reasonCodesReject: state.reasonCodesReject,
		}),
		resetRekey: state => ({
			...state,
			calculated: false,
			rekeyClaim: undefined,
			validationErrors: [],
			statusCalculation: 'beforeCalculation',
			otherServices: [],
		}),
		setCalculated: (state, action) => ({ ...state, calculated: action.payload }),
		setValidationErrors: (state, { payload }) => ({
			...state,
			validationErrors: payload,
		}),
		setStatusCalculation: (state, { payload }: PayloadAction<TStatusCalculation>) => ({
			...state,
			statusCalculation: payload,
		}),
		setOtherServices: (state, { payload }) => ({
			...state,
			otherServices: payload,
		}),
		setReviewField: (state, action) => ({
			...state,
			claimData: {
				...state.claimData,
				receipt: {
					...state.claimData.receipt,
					[action.payload.name]: action.payload.value,
				},
			},
		}),
		resetClaimAttachments: state => ({
			...state,
			claimData: {
				...state.claimData,
				attachments: {
					prescription: undefined,
					others: undefined,
					receipt: undefined,
				},
			},
		}),
		clearClaimAttachment: (state, { payload }) => ({
			...state,
			claimData: {
				...state.claimData,
				attachments:
					payload.docType === 'others'
						? {
								...state.claimData.attachments,
								others: state.claimData.attachments.others?.filter(
									attachment => attachment.uuid !== payload.uuid,
								),
							}
						: {
								...state.claimData.attachments,
								[payload.docType]: undefined,
							},
			},
		}),
		showClaimCommentSection: state => ({
			...state,
			showClaimComment: true,
		}),
		hideClaimCommentSection: state => ({
			...state,
			showClaimComment: false,
		}),
	},
	extraReducers: builder => {
		builder
			.addCase(getClaimDetails.fulfilled, (state, { payload }) => {
				const { servicesMap, comments, ...rest } = payload;

				state.claimData = rest;

				state.claimData.carrierIntegrationFlow =
					rest.carrierIntegrationFlow ?? ECarriersList.BASIC_CARRIER;

				state.claimData.receipt = {
					...state.claimData.receipt,
					...RECEIPT_BASE_DATA,
					retailNetOfTax: extractReviewRetailNetOfTax(rest),
				};
				state.servicesMap = servicesMap;
				state.claimData.comments = comments ?? '';
			})
			.addCase(getClaimDetails.rejected, () => {})
			.addCase(
				calculateEstimates.fulfilled,
				(
					state,
					{
						payload,
					}: {
						payload: {
							//basically everything is passed by calculate. only serviceid and retail amount is not
							components: IClaimComponents[];
							total: IClaimComponents;
							accumulator?: IClaimAccumulator;
						};
					},
				) => {
					return {
						...state,
						calculated: true,
						validationErrors: [],
						rekeyClaim: {
							...state.claimData,
							amount: {
								...payload,
								components: sortServices(payload.components).map(el =>
									// "other" services get a rowId, so rows can be told apart
									state.otherServices.some(
										other => other.serviceId === el.serviceId,
									)
										? { ...el, rowId: uniqueId() }
										: el,
								),
							},
						},
					};
				},
			)
			.addCase(
				calculateEstimates.rejected,
				(state, { payload }: { payload: any }) => {
					state.validationErrors = payload?.data?.validationErrors ?? [];
				},
			)
			.addCase(rekeyClaimAction.fulfilled, state => {
				showSnackBar(`Claim updated with success`, {
					style: { background: palette.primary.main },
				});
				return {
					...state,
					rekeyClaim: undefined,
					statusCalculation: 'beforeCalculation',
					reloadData: true,
				};
			})
			.addCase(updateClaimStatus.fulfilled, (state, { payload }) => {
				showSnackBar(`Claim updated with success`, { variant: 'success' });
				return { ...state, reloadData: true };
			})
			.addCase(createComplaint.fulfilled, (state, { payload }) => {
				showSnackBar(`Claim updated with success`, { variant: 'success' });
				return { ...state, reloadData: true };
			})
			.addCase(createFraud.fulfilled, (state, { payload }) => {
				showSnackBar(`Claim updated with success`, { variant: 'success' });
				return { ...state, reloadData: true };
			})
			.addCase(deleteComplaint.fulfilled, (state, { payload }) => {
				showSnackBar(`Claim updated with success`, { variant: 'success' });
				return { ...state, reloadData: true };
			})
			.addCase(deleteFraud.fulfilled, (state, { payload }) => {
				showSnackBar(`Claim updated with success`, { variant: 'success' });
				return { ...state, reloadData: true };
			})
			.addCase(updateComplaint.fulfilled, (state, { payload }) => {
				showSnackBar(`Claim updated with success`, { variant: 'success' });
				return { ...state, reloadData: true };
			})
			.addCase(updateFraud.fulfilled, (state, { payload }) => {
				showSnackBar(`Claim updated with success`, { variant: 'success' });
				return { ...state, reloadData: true };
			})
			.addCase(getReasonCodeVoid.fulfilled, (state, { payload }) => {
				state.reasonCodesVoid = payload;
			})
			.addCase(getReasonCodeCanceled.fulfilled, (state, { payload }) => {
				state.reasonCodesCanceled = payload;
			})
			.addCase(getReasonCodePending.fulfilled, (state, { payload }) => {
				state.reasonCodesPending = payload;
			})
			.addCase(getReasonCodeReject.fulfilled, (state, { payload }) => {
				state.reasonCodesReject = payload;
			})
			.addCase(voidClaim.fulfilled, state => {
				showSnackBar(`Claim voided with success`, {
					style: { background: palette.primary.main },
				});
				state.reloadData = true;
			})
			.addCase(uploadDocument.fulfilled, (state, action) => {
				const {
					meta: {
						arg: { docType },
					},
				} = action;

				const { uuid, fileName } = action.payload;

				if (['complaint', 'fraud'].includes(docType)) {
					return;
				}

				if (docType !== 'other') {
					state.claimData.attachments[docType] = {
						uuid,
						fileName,
					};
				} else {
					state.claimData.attachments['others'] = [
						...state.claimData.attachments['others']!,
						{ uuid, fileName },
					];
				}
			})
			.addCase(deleteDocument.fulfilled, (state, action) => {
				const {
					meta: {
						arg: { docType, uuid },
					},
				} = action;

				if (['complaint', 'fraud'].includes(docType)) {
					return;
				}

				if (docType !== 'other') {
					state.claimData.attachments[docType] = undefined;
				} else {
					state.claimData.attachments['others'] = state.claimData.attachments[
						'others'
					]?.filter(attachemt => attachemt.uuid !== uuid);
				}
			})
			.addCase(editDocumentName.fulfilled, (state, action) => {
				const {
					meta: {
						arg: { docType, documentUuid },
					},
				} = action;

				if (!state.claimData) return;

				if (['complaint', 'fraud'].includes(docType)) {
					return;
				}

				return {
					...state,
					claimData: {
						...state.claimData,
						attachments: {
							...state.claimData.attachments,
							[docType === 'other' ? 'others' : docType]:
								docType === 'other'
									? [
											...state.claimData.attachments.others!.map(el =>
												el.uuid !== documentUuid
													? el
													: {
															uuid: documentUuid,
															fileName: action.payload.fileName,
														},
											),
										]
									: {
											uuid: documentUuid,
											fileName: action.payload.fileName,
										},
						},
					},
				};
			})
			.addCase(sendToReview.fulfilled, (state, { payload }) => {
				window.location.href = `/view-claims/${payload}`;
			})
			.addCase(returnClaim.fulfilled, state => {
				return { ...state, reloadData: true };
			})
			.addCase(updateClaimComment.fulfilled, (state, action) => {
				return {
					...state,
					claimData: {
						...state.claimData,
						comments: action.meta.arg.comments ?? '', // so that the comment buttons do not appear for a second while the API finishes
					},
					showClaimComment: false,
					reloadData: !state.reloadData,
				};
			});
	},
});

const sortServices = (
	components: IClaim['amount']['components'] | undefined,
): IClaim['amount']['components'] => {
	const sortOreder = [3, 2, 1, 5];
	return components
		? components.sort(
				(a, b) =>
					sortOreder.indexOf(a.serviceId!) - sortOreder.indexOf(b.serviceId!),
			)
		: [];
};

export const {
	resetClaim,
	resetRekey,
	setCalculated,
	setValidationErrors,
	setStatusCalculation,
	setOtherServices,
	setReviewField,
	resetClaimAttachments,
	clearClaimAttachment,
	showClaimCommentSection,
	hideClaimCommentSection,
} = claimSlice.actions;
export default claimSlice.reducer;
